import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchAlbums, fetchPhotos, getPhotoUrl, getCoverImageUrl, deleteAlbum, deletePhoto, updateAlbum, updatePhoto } from '../dataService';
import { hasPermission, Permissions } from '../services/authService';
import ImageGallery from 'react-image-gallery';
import {
    Container,
    Typography,
    Button,
    Box,
    CircularProgress,
    Grid,
    Card,
    CardMedia,
    CardContent,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Menu,
    MenuItem
} from '@mui/material';
import { ArrowBack, Edit as EditIcon, Delete as DeleteIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';

const Album = ({ user, userRole }) => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [subAlbums, setSubAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadedImageCount, setLoadedImageCount] = useState(0);
  const [loadingImages, setLoadingImages] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editSummary, setEditSummary] = useState('');

  const canModifyContent = (ownerId) => {
    return userRole === 'admin' || (user && user.uid === ownerId);
  };

  const canEditAlbum = album && canModifyContent(album.legacy_owner_id);
  const canDeleteAlbum = album && canModifyContent(album.legacy_owner_id);
  const canEditPhoto = (photo) => photo && canModifyContent(photo.legacy_owner_id);
  const canDeletePhoto = (photo) => photo && canModifyContent(photo.legacy_owner_id);

  const preloadImage = (url, photoId) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        setLoadingImages(prev => ({
          ...prev,
          [photoId]: true
        }));
        resolve(url);
      };
      img.onerror = () => {
        setLoadingImages(prev => ({
          ...prev,
          [photoId]: true
        }));
        resolve(url);
      };
      img.src = url;
    });
  };

  useEffect(() => {
    console.log('Current user:', user);
    console.log('Current user role:', userRole);
  }, [user, userRole]);

  useEffect(() => {
    const loadAlbum = async () => {
      setLoading(true);
      setImagesLoaded(false);
      setLoadedImageCount(0);

      const albumsData = await fetchAlbums();
      const photosData = await fetchPhotos();
      const album = albumsData[albumId];
      setAlbum(album);

      // Load sub-albums first
      const subAlbums = await Promise.all(
        Object.keys(albumsData)
          .filter(key => albumsData[key].parent_album === albumId)
          .map(async key => {
            const coverImageUrl = await getCoverImageUrl(albumsData[key].cover_image);
            await preloadImage(coverImageUrl); // Preload sub-album covers
            return {
              id: key,
              coverImageUrl,
              ...albumsData[key]
            };
          })
      );
      setSubAlbums(subAlbums);

      // Then load album photos
      const albumPhotos = await Promise.all(
        Object.keys(photosData)
          .filter(key => photosData[key].parent_album === albumId)
          .map(async key => {
            const photoUrl = await getPhotoUrl(photosData[key].url);
            return {
              id: key,
              original: photoUrl,
              thumbnail: photoUrl,
              description: photosData[key].description
            };
          })
      );
      
      setPhotos(albumPhotos);
      setLoading(false);
      setImagesLoaded(true);

      // Preload images in background
      albumPhotos.forEach(photo => {
        preloadImage(photo.original, photo.id);
      });
    };

    loadAlbum();
  }, [albumId]);

  const handleDeleteAlbum = async () => {
    if (window.confirm('Are you sure you want to delete this album?')) {
      try {
        console.log('Attempting to delete album:', albumId);
        await deleteAlbum(albumId, userRole, user?.uid);
        console.log('Album deleted successfully');
        navigate('/');
      } catch (error) {
        console.error('Error deleting album:', error);
        alert(`Failed to delete album: ${error.message}`);
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      try {
        await deletePhoto(photoId, userRole, user?.uid);
        setPhotos(photos.filter(p => p.id !== photoId));
      } catch (error) {
        console.error('Error deleting photo:', error);
      }
    }
  };

  const handleSavePhotoEdit = async () => {
    try {
      await updatePhoto(editingPhoto.id, {
        description: editingPhoto.description
      }, userRole, user?.uid);
      
      setPhotos(photos.map(p => 
        p.id === editingPhoto.id 
          ? { ...p, description: editingPhoto.description }
          : p
      ));
      setEditingPhoto(null);
    } catch (error) {
      console.error('Error updating photo:', error);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setEditTitle(album.title);
    setEditSummary(album.summary || '');
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      await updateAlbum(albumId, {
        title: editTitle,
        summary: editSummary
      }, userRole, user?.uid);

      setAlbum(prev => ({
        ...prev,
        title: editTitle,
        summary: editSummary
      }));

      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating album:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2
      }}>
        <CircularProgress />
        <Typography>Loading album...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ pt: 10, pb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          component={Link}
          to="/"
          startIcon={<ArrowBack />}
          sx={{ mb: 2 }}
        >
          Back to Gallery
        </Button>
        <Typography variant="h4" sx={{ mb: 1 }}>
          {album?.title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {album?.summary}
        </Typography>
        {(canEditAlbum || canDeleteAlbum) && (
          <>
            <IconButton 
              sx={{ ml: 'auto' }} 
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              {canEditAlbum && (
                <MenuItem onClick={handleEditClick}>
                  <EditIcon sx={{ mr: 1 }} /> Edit Album
                </MenuItem>
              )}
              {canDeleteAlbum && (
                <MenuItem onClick={handleDeleteAlbum}>
                  <DeleteIcon sx={{ mr: 1 }} /> Delete Album
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Box>

      {subAlbums.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Sub Albums
          </Typography>
          <Grid container spacing={3}>
            {subAlbums.map(subAlbum => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={subAlbum.id}>
                <Card
                  component={Link}
                  to={`/album/${subAlbum.id}`}
                  sx={{
                    height: '100%',
                    textDecoration: 'none',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={subAlbum.coverImageUrl}
                    alt={subAlbum.title}
                  />
                  <CardContent>
                    <Typography variant="h6" noWrap>
                      {subAlbum.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {photos.length > 0 && (
        <Box sx={{ 
          '& .image-gallery': {
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto'
          }
        }}>
          <ImageGallery
            items={photos}
            showNav={true}
            showThumbnails={true}
            showFullscreenButton={true}
            showPlayButton={false}
          />
        </Box>
      )}

      {loading && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          mt: 4
        }}>
          <CircularProgress />
          <Typography>Loading images...</Typography>
        </Box>
      )}

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Album</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Summary"
            fullWidth
            multiline
            rows={4}
            value={editSummary}
            onChange={(e) => setEditSummary(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Album;